import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface NotificationSummary {
  total_7d: number;
  delivered_email_7d: number;
  delivered_sms_7d: number;
  delivered_push_7d: number;
  failed_email_7d: number;
  failed_sms_7d: number;
  failed_push_7d: number;
  suppressed_7d: number;
}

export interface NotificationFailureRow {
  id: string;
  event_kind: string | null;
  severity: string;
  title: string;
  property_name: string | null;
  org_name: string | null;
  audience_type: string | null;
  email_error_text: string | null;
  sms_error_text: string | null;
  push_error_text: string | null;
  created_at: string;
}

export interface SuppressedNotification {
  id: string;
  event_kind: string | null;
  severity: string;
  property_name: string | null;
  suppression_reason: string | null;
  created_at: string;
}

export interface ChannelStats {
  attempted: number;
  delivered: number;
  failed: number;
}

export interface ChannelBreakdown {
  email: ChannelStats;
  sms: ChannelStats;
  push: ChannelStats;
}

export interface MonthlyReportFailure {
  id: string;
  property_name: string | null;
  recipient_emails: string[] | null;
  sent_at: string;
}

export interface AudienceBreakdown {
  [audience: string]: number;
}

export interface NotificationDiagnosticsData {
  summary: NotificationSummary;
  recent_failures: NotificationFailureRow[];
  recent_suppressed: SuppressedNotification[];
  channel_breakdown_7d: ChannelBreakdown;
  monthly_report_failures: MonthlyReportFailure[];
  audience_breakdown_7d: AudienceBreakdown;
}

async function fetchNotificationDiagnostics(): Promise<NotificationDiagnosticsData> {
  const { data, error } = await supabase.rpc('admin_notification_diagnostics');
  if (error) throw error;
  return data as NotificationDiagnosticsData;
}

export function useAdminNotificationDiagnostics() {
  return useQuery({
    queryKey: ['admin-notification-diagnostics'],
    queryFn: fetchNotificationDiagnostics,
  });
}
